import Objdict from "objdict"

const GRADIENT_MAX_NUMBER_OF_STOPS = 15 // Same number as on line 16 of spsrite.fragment.glsl when defining the gradientColors array
const loader = {"images": {}}
loader.load = function(source) {
    if(loader.images[source] != undefined) {
        return Promise.resolve(loader.images[source])
    }
    const image = new Image()
    loader.images[source] = image
    loader.images[source].crossOrigin = "anonymous"
    loader.images[source].src = source
    return new Promise((resolve, reject) => {
        image.onload = function() {
            image.isLoaded = true
            resolve(image)
        }
        image.onerror = function() {
            image.isLoaded = false
            console.error(image)
            resolve(image)
        }
    })
}

function createShader(gl, type, source) {
    var shader = gl.createShader(type)
    gl.shaderSource(shader, source)
    gl.compileShader(shader)
    const hasCompiled = gl.getShaderParameter(shader, gl.COMPILE_STATUS)
    if(hasCompiled == false) {
        console.log(gl.getShaderInfoLog(shader))
        gl.deleteShader(shader)
        return
    }

    return shader
}
function createProgram(gl, vertexShader, fragmentShader) {
    var program = gl.createProgram()
    gl.attachShader(program, vertexShader)
    gl.attachShader(program, fragmentShader)
    gl.linkProgram(program)
    var hasCompiled = gl.getProgramParameter(program, gl.LINK_STATUS)
    if(hasCompiled == false) {
        console.log(gl.getProgramInfoLog(program))
        gl.deleteProgram(program)
        return
    }
    return program
}

// input
// {
//     "shaders": {
//         "vertex": "GLSL SOURCE CODE",
//         "fragment": "GLSL SOURCE CODE",
//     },
//     "attributes": ["ARRAY", "OF", "NAMES"],
//     "uniforms": ["ARRAY", "OF", "NAMES"],
// }
// output
// {
//     "program": WEBGL PROGRAM,
//     "shaders": {
//         "vertex": WEBGL SHADER,
//         "fragment": WEBGL SHADER,
//     },
//     "attributes": {
//         "ARRAY": WEBGL LOCATION,
//         "OF": WEBGL LOCATION,
//         "NAMES": WEBGL LOCATION
//     },
//     "uniforms": {
//         ...
//     }
// }
function createBundle(gl, bundle) {
    bundle.shaders.vertex = createShader(gl, gl.VERTEX_SHADER, bundle.shaders.vertex)
    bundle.shaders.fragment = createShader(gl, gl.FRAGMENT_SHADER, bundle.shaders.fragment)
    bundle.program = createProgram(gl, bundle.shaders.vertex, bundle.shaders.fragment)

    bundle.attributes = bundle.attributes.map((attribute) => {
        return {"key": attribute, "location": gl.getAttribLocation(bundle.program, attribute)}
    })
    bundle.uniforms = bundle.uniforms.map((uniform) => {
        return {"key": uniform, "location": gl.getUniformLocation(bundle.program, uniform)}
    })

    bundle.attributes = Objdict.map(Objdict.convert(bundle.attributes), (attribute) => attribute.location)
    bundle.uniforms = Objdict.map(Objdict.convert(bundle.uniforms), (uniform) => uniform.location)

    return bundle
}

////////////////////
// INSTANTIATING //
//////////////////

export default class Renderer {
    constructor(canvas, key) {
        this.gl = undefined
        this.key = key
        this.start(canvas)
    }
    start(canvas) {
        // gl
        this.gl = canvas.getContext("webgl2", {
            "alpha": true,
        })
        if(this.gl === undefined) throw new Error("webgl2 is not supported :(")

        // program setup
        // pointers
        this.bundles = {
            "sprite": createBundle(this.gl, {
                "shaders": {
                    "vertex": require("./shaders/sprite.vertex.glsl").default,
                    "fragment": require("./shaders/sprite.fragment.glsl").default,
                },
                "attributes": ["position", "textureCoord"],
                "uniforms": [
                    "resolution",
                    "u_textureMatrix",
                    "gradientColorsCount", "gradientColors",
                    "tintIntensity", "tintColor",
                    "outlineColor", "outlineThickness",
                    "grayscaleIntensity",
                    "spriteSize",
                    "opacity",
                    "isMasked", "maskImage", "imagePosition",
                    "isFlipped",
                    "isPaletteSwapped", "paletteImage",
                    "textureImage"
                ],
            }),
            "rectangle": createBundle(this.gl, {
                "shaders": {
                    "vertex": require("./shaders/rectangle.vertex.glsl").default,
                    "fragment": require("./shaders/rectangle.fragment.glsl").default,
                },
                "attributes": ["position"],
                "uniforms": ["resolution", "color"],
            }),
        }

        // vertex array ??
        this.vao = this.gl.createVertexArray()
        this.gl.bindVertexArray(this.vao)

        // position
        this.positionBuffer = this.gl.createBuffer()

        // view port
        this.gl.viewport(0, 0, this.gl.canvas.width, this.gl.canvas.height)

        this.gl.enable(this.gl.BLEND)
        this.gl.blendEquationSeparate(this.gl.FUNC_ADD, this.gl.MAX)
    }
    //////////////
    // LOADING //
    ////////////
    processImageIntoTexture(image) {
        const texCoordBuffer = this.gl.createBuffer()
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, texCoordBuffer)
        this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array([0.0,  0.0, 1.0,  0.0, 0.0,  1.0, 0.0,  1.0, 1.0,  0.0, 1.0,  1.0]), this.gl.STATIC_DRAW)
        this.gl.enableVertexAttribArray(this.bundles["sprite"].attributes["textureCoord"])
        var size = 2          // 2 components per iteration
        var type = this.gl.FLOAT   // the data is 32bit floats
        var normalize = false // don't normalize the data
        var stride = 0        // 0 = move forward size * sizeof(type) each iteration to get the next position
        var offset = 0        // start at the beginning of the buffer
        this.gl.vertexAttribPointer(this.bundles["sprite"].attributes["textureCoord"], size, type, normalize, stride, offset)

        // Create a texture.
        var texture = this.gl.createTexture()

        // make unit 0 the active texture uint
        // (ie, the unit all other texture commands will affect
        this.gl.activeTexture(this.gl.TEXTURE0)
        image.index = 0

        // Bind it to texture unit 0' 2D bind point
        this.gl.bindTexture(this.gl.TEXTURE_2D, texture)

        // Set the parameters so we don't need mips and so we're not filtering
        // and we don't repeat
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE)
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE)
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST)
        this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST)

        // Upload the image into the texture.
        var mipLevel = 0               // the largest mip
        var internalFormat = this.gl.RGBA   // format we want in the texture
        var srcFormat = this.gl.RGBA        // format of data we are supplying
        var srcType = this.gl.UNSIGNED_BYTE  // type of data we are supplying
        this.gl.texImage2D(this.gl.TEXTURE_2D, mipLevel, internalFormat, srcFormat, srcType, image)
        if(image.texture == undefined) image.texture = {}
        image.texture[this.key] = texture
    }
    /////////////////
    // PERFORMING //
    ///////////////
    clear() {
        this.gl.clearColor(0, 0, 0, 0)
        this.gl.clear(this.gl.COLOR_BUFFER_BIT)
    }
    render(renderable) {
        // inputs into this:
        // {
        //     image,
        //     color,
        //     opacity,
        //     tint,
        //     grayscale,
        //     bounds: {
        //         x1
        //         x2
        //         y1
        //         y2,
        //         width, // ??
        //         height, // ??
        //     }
        // }
        // everything else should be done in canvas/index.js

        if(renderable.blendmode == "cool") {
            this.gl.blendFunc(this.gl.ONE, this.gl.ONE_MINUS_SRC_ALPHA)
        } else if(renderable.blendmode == "multiply") {
            // this.gl.blendFunc(this.gl[blendmodes[0].a], this.gl[blendmodes[0].b])
            this.gl.blendFunc(this.gl.ZERO, this.gl.SRC_COLOR)
            // this.gl.blendFuncSeparate(this.gl.ZERO, this.gl.SRC_COLOR, this.gl.ZERO, this.gl.ONE)
        } else {
            this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA) // source is NOT premultiplied alpha
            // this.gl.blendFuncSeparate(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA, this.gl.ZERO, this.gl.ONE) // is NOT premultiplied AND I'm not leaking opacity.
            // this.gl.blendFunc(this.gl.ONE, this.gl.ONE_MINUS_SRC_ALPHA) // source IS premultiplied alpha
        }

        if(renderable.color != undefined) {
            this.gl.useProgram(this.bundles["rectangle"].program)
            this.gl.bindVertexArray(this.vao)

            this.gl.uniform2f(this.bundles["rectangle"].uniforms["resolution"], this.gl.canvas.width, this.gl.canvas.height)

            if(renderable.color == undefined) renderable.color = "#FF69B4"
            if(renderable.opacity == undefined) renderable.opacity = 1
            // renderable.color = parseColor(renderable.color)
            this.gl.uniform4f(this.bundles["rectangle"].uniforms["color"], ...renderable.color, renderable.opacity)

            // if(renderable.radius != undefined) {
            //     renderable.width = renderable.radius * 2
            //     renderable.height = renderable.radius * 2
            //     renderable.position = renderable.position || {}
            //     renderable.position.x = renderable.position.x || 0
            //     renderable.position.y = renderable.position.y || 0
            //     // renderable.position.x -= renderable.radius
            //     // renderable.position.y -= renderable.radius
            // }
            // if(renderable.width == undefined
            // || renderable.height == undefined) {
            //     return
            // }

            const points = getRectanglePoints(renderable)
            this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.positionBuffer)
            this.gl.enableVertexAttribArray(this.bundles["rectangle"].attributes["position"])
            this.gl.vertexAttribPointer(this.bundles["rectangle"].attributes["position"], 2, this.gl.FLOAT, false, 0, 0)
            this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(points), this.gl.STATIC_DRAW)
            this.gl.drawArrays(this.gl.TRIANGLES, 0, 6)
        }

        if(renderable.image != undefined) {

            if(renderable.image == undefined) {
                console.log(renderable)
                throw new Error("Could not find renderable.image")
            }

            if(renderable.image.default != undefined) {
                console.log(renderable)
                throw new Error("Could not handle a non-defaulted image import")
            }

            if(renderable.image.isLoaded != true
            || renderable.image.texture[this.key] == undefined) {
                return
            }

            this.gl.useProgram(this.bundles["sprite"].program)
            this.gl.bindVertexArray(this.vao)

            this.gl.uniform1i(this.bundles["sprite"].uniforms["textureImage"], 0)
            this.gl.activeTexture(this.gl.TEXTURE0)
            this.gl.bindTexture(this.gl.TEXTURE_2D, renderable.image.texture[this.key])
            this.gl.uniform2f(this.bundles["sprite"].uniforms["resolution"], this.gl.canvas.width, this.gl.canvas.height)
            this.gl.uniform2f(this.bundles["sprite"].uniforms["spriteSize"], renderable.image.width, renderable.image.height)
            // this.gl.uniform1i(this.imageUniformLocation, renderable.image.index)

            if(renderable.opacity == undefined) renderable.opacity = 1
            this.gl.uniform1f(this.bundles["sprite"].uniforms["opacity"], renderable.opacity)

            if(renderable.tint != undefined) {
                // renderable.tint.color = parseColor(renderable.tint.color)
                if(renderable.tint.opacity == undefined) renderable.tint.opacity = 1
                if(renderable.tint.intensity == undefined) renderable.tint.intensity = 1
                this.gl.uniform4f(this.bundles["sprite"].uniforms["tintColor"], ...renderable.tint.color, renderable.tint.opacity)
                this.gl.uniform1f(this.bundles["sprite"].uniforms["tintIntensity"], renderable.tint.intensity)
            } else {
                this.gl.uniform4f(this.bundles["sprite"].uniforms["tintColor"], 0, 0, 0, 0)
                this.gl.uniform1f(this.bundles["sprite"].uniforms["tintIntensity"], 0)
            }

            if(renderable.sourceBounds != undefined) {
                const translationMatrix = matrixMath.translation(renderable.sourceBounds.x / renderable.image.width, renderable.sourceBounds.y / renderable.image.height)
                const scaleMatrix = matrixMath.scaling(renderable.sourceBounds.width / renderable.image.width, renderable.sourceBounds.height / renderable.image.height)
                const texMatrix = matrixMath.multiply(translationMatrix, scaleMatrix)

                this.gl.uniformMatrix3fv(this.bundles["sprite"].uniforms["u_textureMatrix"], false, texMatrix)
            } else {
                this.gl.uniformMatrix3fv(this.bundles["sprite"].uniforms["u_textureMatrix"], false, [1,0,0,0,1,0,0,0,1])
            }

            if(renderable.gradientMapping != undefined) {
                this.gl.uniform3fv(this.bundles["sprite"].uniforms["gradientColors"], renderable.gradientMapping.colors.flat().map((value) => value / 255.0))
                this.gl.uniform1f(this.bundles["sprite"].uniforms["gradientColorsCount"], renderable.gradientMapping.colors.length)
            } else {
                this.gl.uniform3fv(this.bundles["sprite"].uniforms["gradientColors"], [0, 0, 0])
                this.gl.uniform1f(this.bundles["sprite"].uniforms["gradientColorsCount"], 0)
            }

            if(renderable.grayscale != undefined) {
                renderable.grayscale.value = renderable.grayscale.value || 0
                this.gl.uniform1f(this.bundles["sprite"].uniforms["grayscaleIntensity"], renderable.grayscale.value)
            } else {
                this.gl.uniform1f(this.bundles["sprite"].uniforms["grayscaleIntensity"], 0)
            }

            if(renderable.outline != undefined) {
                // renderable.outline.color = parseColor(renderable.outline.color)
                if(renderable.outline.opacity == undefined) renderable.outline.opacity = 1
                if(renderable.outline.thickness == undefined) renderable.outline.thickness = 1
                this.gl.uniform4f(this.bundles["sprite"].uniforms["outlineColor"], ...renderable.outline.color, renderable.outline.opacity)
                this.gl.uniform1f(this.bundles["sprite"].uniforms["outlineThickness"], renderable.outline.thickness)
            } else {
                this.gl.uniform4f(this.bundles["sprite"].uniforms["outlineColor"], 0, 0, 0, 0)
                this.gl.uniform1f(this.bundles["sprite"].uniforms["outlineThickness"], 0)
            }

            if(renderable.isFlipped == true) {
                this.gl.uniform1f(this.bundles["sprite"].uniforms["isFlipped"], 1)
            } else {
                this.gl.uniform1f(this.bundles["sprite"].uniforms["isFlipped"], 0)
            }

            this.gl.uniform2f(this.bundles["sprite"].uniforms["imagePosition"], renderable.bounds.x1, renderable.bounds.y1)
            if(renderable.mask == true) {
                const maskImage = require("@frog/source/assets/images/apocalypse-tactics/shadow-320-to-384.png").default
                if(loader.images[maskImage] == undefined) {
                    loader.load({"images": maskImage}).then((image) => {
                        this.processImageIntoTexture(image)
                    })
                }
                if(loader.images[maskImage].isLoaded == true
                && loader.images[maskImage].isProcessedIntoTexture == true) {
                    this.gl.uniform1f(this.bundles["sprite"].uniforms["isMasked"], 1)
                    this.gl.uniform1i(this.bundles["sprite"].uniforms["maskImage"], 1)
                    this.gl.activeTexture(this.gl.TEXTURE1)
                    this.gl.bindTexture(this.gl.TEXTURE_2D, loader.images[maskImage].texture)
                }
            } else {
                this.gl.uniform1f(this.bundles["sprite"].uniforms["isMasked"], 0)
            }

            this.gl.uniform1f(this.bundles["sprite"].uniforms["isPaletteSwapped"], 0)
            if(renderable.palette != undefined) {
                if(renderable.palette.default != undefined) {
                    console.log(renderable.palette)
                    throw new Error("Could not handle a non-defaulted palette image import")
                }
                if(loader.images[renderable.palette] == undefined) {
                    loader.load({"images": renderable.palette}).then((image) => {
                        this.processImageIntoTexture(image)
                    })
                }
                if(loader.images[renderable.palette].isLoaded == true
                && loader.images[renderable.palette].isProcessedIntoTexture == true) {
                    this.gl.uniform1f(this.bundles["sprite"].uniforms["isPaletteSwapped"], 1)

                    this.gl.uniform1i(this.bundles["sprite"].uniforms["paletteImage"], 1)
                    this.gl.activeTexture(this.gl.TEXTURE1)
                    this.gl.bindTexture(this.gl.TEXTURE_2D, loader.images[renderable.palette].texture)
                }
            }

            const points = getRectanglePoints(renderable)
            this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.positionBuffer)
            this.gl.enableVertexAttribArray(this.bundles["sprite"].attributes["position"])
            this.gl.vertexAttribPointer(this.bundles["sprite"].attributes["position"], 2, this.gl.FLOAT, false, 0, 0)
            this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(points), this.gl.STATIC_DRAW)
            this.gl.drawArrays(this.gl.TRIANGLES, 0, 6)
        }
    }
    drawImage(options) {
        let gradientMapping = undefined
        if(options.colors != undefined) {
            options.colors.forEach((color) => {
                color.L = getLightness(color)
            })
            options.colors.sort((a, b) => a.L - b.L)
            if(options.colors.length > GRADIENT_MAX_NUMBER_OF_STOPS) options.colors = palette.colors.slice(0, GRADIENT_MAX_NUMBER_OF_STOPS)
            gradientMapping = {
                "colors": options.colors.map((color) => [color.r, color.g, color.b]),
            }
        }
        if(options.source != undefined) {
            this.render({
                "image": options.image,
                "gradientMapping": gradientMapping,
                "sourceBounds": {
                    "x": options.source.x,
                    "y": options.source.y,
                    "width": options.source.w,
                    "height": options.source.h,
                },
                "bounds": {
                    "x1": options.destination.x,
                    "y1": options.destination.y,
                    "x2": options.destination.x + options.destination.w,
                    "y2": options.destination.y + options.destination.h,
                }
            })
        } else if(options.destination.w != undefined) {
            this.render({
                "image": options.image,
                "gradientMapping": gradientMapping,
                "bounds": {
                    "x1": options.destination.x,
                    "y1": options.destination.y,
                    "x2": options.destination.x + options.destination.w,
                    "y2": options.destination.y + options.destination.h,
                }
            })
        } else {
            this.render({
                "image": options.image,
                "gradientMapping": gradientMapping,
                "bounds": {
                    "x1": options.destination.x,
                    "y1": options.destination.y,
                    "x2": options.destination.x + options.image.width,
                    "y2": options.destination.y + options.image.height,
                }
            })
        }
    }
}

function getLightness(color) {
    return (Math.max(color.r, color.g, color.b) + Math.min(color.r, color.g, color.b)) / (255 * 2)
}

function getRectanglePoints(entity) {
    const x1 = Math.round(entity.bounds.x1)
    const y1 = Math.round(entity.bounds.y1)
    const x2 = Math.round(entity.bounds.x2)
    const y2 = Math.round(entity.bounds.y2)
    return [
        x1, y1,
        x2, y1,
        x1, y2,
        x1, y2,
        x2, y1,
        x2, y2
    ]
}

const matrixMath = {
    "multiply": function(a, b) {
        var a00 = a[0 * 3 + 0]
        var a01 = a[0 * 3 + 1]
        var a02 = a[0 * 3 + 2]
        var a10 = a[1 * 3 + 0]
        var a11 = a[1 * 3 + 1]
        var a12 = a[1 * 3 + 2]
        var a20 = a[2 * 3 + 0]
        var a21 = a[2 * 3 + 1]
        var a22 = a[2 * 3 + 2]
        var b00 = b[0 * 3 + 0]
        var b01 = b[0 * 3 + 1]
        var b02 = b[0 * 3 + 2]
        var b10 = b[1 * 3 + 0]
        var b11 = b[1 * 3 + 1]
        var b12 = b[1 * 3 + 2]
        var b20 = b[2 * 3 + 0]
        var b21 = b[2 * 3 + 1]
        var b22 = b[2 * 3 + 2]

        return [
            b00 * a00 + b01 * a10 + b02 * a20,
            b00 * a01 + b01 * a11 + b02 * a21,
            b00 * a02 + b01 * a12 + b02 * a22,
            b10 * a00 + b11 * a10 + b12 * a20,
            b10 * a01 + b11 * a11 + b12 * a21,
            b10 * a02 + b11 * a12 + b12 * a22,
            b20 * a00 + b21 * a10 + b22 * a20,
            b20 * a01 + b21 * a11 + b22 * a21,
            b20 * a02 + b21 * a12 + b22 * a22,
        ]
    },
    "translation": function(tx, ty) {
        return [
            1, 0, 0,
            0, 1, 0,
            tx, ty, 1,
        ]
    },
    "rotation": function(angleInRadians) {
        var c = Math.cos(angleInRadians)
        var s = Math.sin(angleInRadians)
        return [
            c,-s, 0,
            s, c, 0,
            0, 0, 1,
        ]
    },
    "scaling": function(sx, sy) {
        return [
            sx, 0, 0,
            0, sy, 0,
            0, 0, 1,
        ]
    },
}
